/**
 * Date-range arithmetic on ISO ranges — the prototype's reducer helpers,
 * moved out of React so the Redux filters slice can use them.
 */
import { addDays, orderRange, startOfDay } from '@deepdive-ui';

import type { DateRangeValue } from '@/data-sources/contract/filters';

import { fromIsoDate, toIsoDate, type IsoDate } from './dates';

const DAY = 86_400_000;

/** The prototype's default window: the last 70 days, ending today. */
export const DEFAULT_RANGE_DAYS = 70;

export function defaultRange(today: IsoDate): DateRangeValue {
  const end = fromIsoDate(today);
  return { start: toIsoDate(addDays(end, -(DEFAULT_RANGE_DAYS - 1))), end: today };
}

/** Order the ends and drop any time of day. */
export function normaliseRange(range: DateRangeValue): DateRangeValue {
  const ordered = orderRange({
    start: range.start ? fromIsoDate(range.start) : null,
    end: range.end ? fromIsoDate(range.end) : null,
  });
  return {
    start: ordered.start ? toIsoDate(startOfDay(ordered.start)) : null,
    end: ordered.end ? toIsoDate(startOfDay(ordered.end)) : null,
  };
}

/** The same number of days immediately before `range`; null for an open range. */
export function previousPeriod(range: DateRangeValue): DateRangeValue | null {
  const { start, end } = normaliseRange(range);
  if (!start || !end) return null;
  const from = fromIsoDate(start);
  const days = Math.round((fromIsoDate(end).getTime() - from.getTime()) / DAY) + 1;
  return { start: toIsoDate(addDays(from, -days)), end: toIsoDate(addDays(from, -1)) };
}

/* ------------------------------------------- calendar months (executive view) */

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

type Ymd = { y: number; m: number; d: number };

/* String arithmetic on `YYYY-MM-DD`, never a `Date`: no time zone can move a day. */
const ymd = (iso: IsoDate): Ymd => {
  const [y, m, d] = iso.split('-').map(Number);
  return { y: y!, m: m!, d: d! };
};
const isoOf = ({ y, m, d }: Ymd): IsoDate =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** The 1st of `today`'s month to `today` — "this month" so far. */
export function monthToDate(today: IsoDate): DateRangeValue {
  return { start: isoOf({ ...ymd(today), d: 1 }), end: today };
}

/**
 * What the executive view compares a range with. A range that starts on the
 * 1st and stays inside one month is "this month so far" or a whole month, and
 * is set against the same days of the month before — the whole of it for a
 * whole month, else day 1 to the same day, cut at that month's end (Mar 1–30
 * → Feb 1–28). Any other range keeps `previousPeriod`.
 */
export function comparisonFor(range: DateRangeValue): DateRangeValue | null {
  const { start, end } = normaliseRange(range);
  if (!start || !end) return null;
  const from = ymd(start);
  const to = ymd(end);
  if (from.d !== 1 || from.y !== to.y || from.m !== to.m) return previousPeriod(range);
  const before = from.m === 1 ? { y: from.y - 1, m: 12 } : { y: from.y, m: from.m - 1 };
  const length = daysIn(before.y, before.m);
  const lastDay = to.d === daysIn(to.y, to.m) ? length : Math.min(to.d, length);
  return { start: isoOf({ ...before, d: 1 }), end: isoOf({ ...before, d: lastDay }) };
}

/**
 * A range as a sentence names it: `September 2026` for a whole month,
 * `Sep 1–27` inside one, `Aug 4 – Sep 30` across two, with the year added when
 * the ends fall in different years. `null` for an open range.
 */
export function periodLabel(range: DateRangeValue | null): string | null {
  if (!range?.start || !range.end) return null;
  const from = ymd(range.start);
  const to = ymd(range.end);
  const short = (p: Ymd) => MONTHS[p.m - 1]!.slice(0, 3);
  if (from.y === to.y && from.m === to.m) {
    if (from.d === 1 && to.d === daysIn(to.y, to.m)) return `${MONTHS[from.m - 1]} ${from.y}`;
    return from.d === to.d ? `${short(from)} ${from.d}` : `${short(from)} ${from.d}–${to.d}`;
  }
  if (from.y === to.y) return `${short(from)} ${from.d} – ${short(to)} ${to.d}`;
  return `${short(from)} ${from.d}, ${from.y} – ${short(to)} ${to.d}, ${to.y}`;
}

const ENGINE_RANGE = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;

/**
 * An engine scope as its first and last day: a range (`2026-08-01..2026-08-31`),
 * a month (`2026-08`) or a day (`2026-08-05`). Anything else is not a period.
 */
export function scopeBounds(scope: string): { start: string; end: string } | undefined {
  const text = scope.trim();
  const range = ENGINE_RANGE.exec(text);
  if (range) return { start: range[1]!, end: range[2]! };
  const month = /^(\d{4})-(\d{2})$/.exec(text);
  if (month) {
    const last = daysIn(Number(month[1]), Number(month[2]));
    return { start: `${text}-01`, end: `${text}-${String(last).padStart(2, '0')}` };
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? { start: text, end: text } : undefined;
}

/** An engine scope as `periodLabel` names it; any other scope as given. */
export function scopeLabel(scope: string): string {
  const bounds = scopeBounds(scope);
  return (bounds && periodLabel(bounds)) ?? scope.trim();
}

export function toPickerRange(range: DateRangeValue): { start: Date | null; end: Date | null } {
  return {
    start: range.start ? fromIsoDate(range.start) : null,
    end: range.end ? fromIsoDate(range.end) : null,
  };
}

export function fromPickerRange(range: { start: Date | null; end: Date | null }): DateRangeValue {
  return {
    start: range.start ? toIsoDate(range.start) : null,
    end: range.end ? toIsoDate(range.end) : null,
  };
}
